import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import CoopPriceProjectionHelper from '@app/Service/CoopPriceProjectionHelper';
import CoopPriceProjectionInterface from '@app/Interface/CoopPriceProjectionInterface';
import WeightedAverageHelper from '@app/Service/WeightedAverageHelper';
import WeightedAverageInterface from '@app/Interface/WeightedAverageInterface';
import ItemLoader from '@app/Components/ItemLoader';
import Calculator from '@app/Components/Report/ProjectedMilkValue/Calculator';

interface ProjectedMilkValueReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    setReportTableRef: React.Dispatch<React.SetStateAction<React.MutableRefObject<null> | null>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
}


const ProjectedMilkValueReport: React.FC<ProjectedMilkValueReportProps> = ({ pageId, setLastUpdatedText }) => {

    const { userState } = React.useContext(UserContext);

    const [coopPriceProjectionsLoading, setCoopPriceProjectionsLoading] = React.useState(true);
    const [coopPriceProjections, setCoopPriceProjections] = React.useState<CoopPriceProjectionInterface[]>([]);
    const [weightedAverageLoading, setWeightedAverageLoading] = React.useState(true);
    const [weightedAverage, setWeightedAverage] = React.useState<WeightedAverageInterface|null>(null);

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'ndaWeb_WeightedAvg')
            .then(text => { if (mounted) setLastUpdatedText(text)});

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // Get price projections
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function fetchPriceProjectionsData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await CoopPriceProjectionHelper.getPriceProjections(
                    controller, 
                    userState.user.currentFpuId, 
                    pageId,
                    'milkPricing'
                );
                if (mounted) {
                    setCoopPriceProjections(rows);
                    setCoopPriceProjectionsLoading(false);
                }
            } else {
                if (mounted) {
                    setCoopPriceProjections([]);
                    setCoopPriceProjectionsLoading(false);
                }
            }
        }

        fetchPriceProjectionsData();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new weighted average data
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function fetchWeightedAverageData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await WeightedAverageHelper.getAverages(
                    controller,
                    userState.user.currentFpuId,
                    pageId
                );
                if (mounted) {
                    setWeightedAverage(rows[0] ?? null);
                    setWeightedAverageLoading(false);
                }
            } else {
                if (mounted) {
                    setWeightedAverage(null);
                    setWeightedAverageLoading(false);
                }
            }
        }

        setWeightedAverageLoading(true);
        fetchWeightedAverageData();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    if (coopPriceProjectionsLoading || weightedAverageLoading) {
        return <ItemLoader />;
    }

    if (coopPriceProjections.length === 0) {
        return <p><em>Price projections are currently unavailable. If this problem persists, please contact an administrator.</em></p>;
    }

    return (
        <Calculator coopPriceProjections={coopPriceProjections} weightedAverage={weightedAverage} />
    );
};

export default ProjectedMilkValueReport;